import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import {
  makeApi,
  type Issue,
  type IssuePriority,
  type IssueStatus,
  type AdminProject,
} from '../api/client';
import { IssuesTable } from '../components/IssuesTable';

// Pseudo-status used in the UI dropdown only. 'active' = open + in_progress
// (excludes resolved). Translated to backend params + client-side filter in
// load(); the backend doesn't have a native multi-status filter.
type StatusFilter = 'active' | IssueStatus | 'all';

export function AdminIssuesPage(): JSX.Element {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const navigate = useNavigate();
  const [issues, setIssues] = useState<Issue[]>([]);
  const [projects, setProjects] = useState<AdminProject[]>([]);
  const [projectId, setProjectId] = useState('');
  // Default to "active" — staff opens /admin/issues to triage live work, not
  // to look at the resolved archive. Resolved still reachable via the
  // dropdown.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [priority, setPriority] = useState<'' | IssuePriority>('');
  const [customerSub, setCustomerSub] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.adminListProjects().then((r) => setProjects(r.projects)).catch(() => {});
  }, []);

  async function load(): Promise<void> {
    setLoading(true);
    try {
      // Backend takes a single status string. For 'active' (the default) and
      // 'all' we send no status param and filter client-side; for the
      // explicit single statuses we let the backend filter.
      const backendStatus: IssueStatus | undefined =
        statusFilter === 'active' || statusFilter === 'all' ? undefined : statusFilter;
      const r = await api.adminListIssues({
        projectId: projectId || undefined,
        status: backendStatus,
        priority: priority || undefined,
        customerSub: customerSub || undefined,
      });
      const filtered =
        statusFilter === 'active'
          ? r.issues.filter((i) => i.status !== 'resolved')
          : r.issues;
      setIssues(filtered);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [projectId, statusFilter, priority]);

  // Counts derived from the loaded issue list — single source of truth, can't
  // mismatch with the table by construction.
  const awaitingMeCount = useMemo(
    () =>
      issues.filter(
        (i) => i.awaitingAction === 'staff' && i.status !== 'resolved',
      ).length,
    [issues],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-white">Issues</h1>
        <button
          type="button"
          className="btn-primary text-sm"
          onClick={() => navigate('/admin/issues/new')}
        >
          + New issue
        </button>
      </div>

      {/* Existing filters */}
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <label className="label">Project</label>
          <select className="input" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">All projects</option>
            {projects.map((p) => (
              <option key={p.pid} value={p.pid}>
                {p.displayName} ({p.projectReferenceNumber})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Status</label>
          <select
            className="input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            <option value="active">Active (open + in progress)</option>
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="resolved">Resolved</option>
            <option value="all">All (incl. resolved)</option>
          </select>
        </div>
        <div>
          <label className="label">Priority</label>
          <select className="input" value={priority} onChange={(e) => setPriority(e.target.value as IssuePriority | '')}>
            <option value="">All</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>
        <div className="w-full sm:flex-1 sm:min-w-[200px]">
          <label className="label">Customer sub</label>
          <input
            className="input"
            value={customerSub}
            onChange={(e) => setCustomerSub(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
          />
        </div>
      </div>

      {/* Single client-side counter — derived from `issues`. */}
      {!loading && issues.length > 0 && (
        <div className="text-sm text-gray-400" data-testid="awaiting-me-count">
          {issues.length} issue{issues.length === 1 ? '' : 's'}
          {awaitingMeCount > 0 && (
            <> — <span className="text-red-400">{awaitingMeCount} awaiting you</span></>
          )}
        </div>
      )}

      {error && <div className="text-sm text-red-400">{error}</div>}
      {loading ? (
        <div className="text-gray-500">Loading…</div>
      ) : (
        <IssuesTable
          issues={issues}
          showReporter
          buildIssueHref={(i) =>
            `/admin/issues/${i.issueId}?projectId=${encodeURIComponent(i.projectId)}&addressId=${encodeURIComponent(i.addressId)}`
          }
        />
      )}
    </div>
  );
}
